import { supabase, supabaseUrl } from "./supabaseClient";

export type UploadFolder =
  | "jobs"
  | "deliveries"
  | "media"
  | "branding"
  | "website"
  | "invitations"
  | "seo"
  | "signature_books"
  | "generic";

export interface VpsUploadOptions {
  file: File;
  tenantId: string;
  entityType?: string;
  entityId?: string;
  module?: string;
  /** Folder/category for organization on the VPS */
  folder?: UploadFolder;
  /** Client-side validation: allowed MIME types. Checked before upload starts. */
  allowedTypes?: string[];
  /** Client-side validation: max file size in MB. Checked before upload starts. */
  maxSizeMB?: number;
}

export interface VpsUploadResult {
  success: boolean;
  url?: string;
  path?: string;
  filename?: string;
  size?: number;
  mime_type?: string;
  fileId?: string;
  error?: string;
}

export interface VpsDeleteOptions {
  fileId?: string;
  path?: string;
  tenantId: string;
  /** If true, do not throw — return { success: false, error: string } instead */
  silent?: boolean;
}

export interface VpsDeleteResult {
  success: boolean;
  message?: string;
  deletedId?: string;
  error?: string;
}

/* ─── Validation helpers ────────────────────────────────────── */

function validateUpload(options: VpsUploadOptions): { ok: true } | { ok: false; error: string } {
  if (options.maxSizeMB && options.file.size > options.maxSizeMB * 1024 * 1024) {
    return {
      ok: false,
      error: `File too large (${(options.file.size / 1024 / 1024).toFixed(1)} MB). Max allowed: ${options.maxSizeMB} MB`,
    };
  }
  if (options.allowedTypes && options.allowedTypes.length > 0) {
    const allowed = options.allowedTypes.some(
      (t) => options.file.type === t || t.endsWith("/*") && options.file.type.startsWith(t.replace("/*", "")),
    );
    if (!allowed) {
      return {
        ok: false,
        error: `File type not allowed: ${options.file.type || "unknown"}. Allowed: ${options.allowedTypes.join(", ")}`,
      };
    }
  }
  return { ok: true };
}

/* ─── Legacy wrapper — preserved for backward compatibility ──── */

export async function uploadToVps(options: VpsUploadOptions): Promise<VpsUploadResult & { fileName: string; fileSizeBytes: number; mimeType: string }> {
  const result = await uploadFileToVps(options);
  if (!result.success || !result.url) {
    throw new Error(result.error || "Upload failed");
  }
  return {
    success: true,
    url: result.url,
    path: result.path ?? "",
    fileName: result.filename ?? "",
    fileSizeBytes: result.size ?? 0,
    mimeType: result.mime_type ?? "",
    fileId: result.fileId,
  };
}

export async function deleteFromVps(options: VpsDeleteOptions): Promise<VpsDeleteResult & { message: string }> {
  const result = await deleteFileFromVps(options);
  if (!result.success) {
    throw new Error(result.error || "Delete failed");
  }
  return {
    success: true,
    message: result.message ?? "Deleted",
    deletedId: result.deletedId,
  };
}

/* ─── Core service: uploadFileToVps ─────────────────────────── */

export async function uploadFileToVps(options: VpsUploadOptions): Promise<VpsUploadResult> {
  const validation = validateUpload(options);
  if (!validation.ok) {
    return { success: false, error: validation.error };
  }

  const { data: session } = await supabase.auth.getSession();
  const token = session?.session?.access_token;

  if (!token) {
    return { success: false, error: "User not authenticated" };
  }

  const formData = new FormData();
  formData.append("photo", options.file);
  formData.append("tenant_id", options.tenantId);
  if (options.entityType) formData.append("entity_type", options.entityType);
  if (options.entityId) formData.append("entity_id", options.entityId);
  if (options.module) formData.append("client_id", options.module);
  if (options.folder) formData.append("folder", options.folder);

  const functionUrl = `${supabaseUrl}/functions/v1/vps-upload-proxy`;

  try {
    const res = await fetch(functionUrl, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });

    const data = await res.json();

    if (!res.ok) {
      return {
        success: false,
        error: data?.error || `Upload failed (HTTP ${res.status})`,
      };
    }

    if (!data.success) {
      return {
        success: false,
        error: data?.error || "VPS rejected the upload",
      };
    }

    return {
      success: true,
      url: data.vps?.url,
      path: data.vps?.path,
      filename: data.vps?.file_name ?? data.vps?.filename ?? options.file.name,
      size: data.vps?.file_size_bytes ?? options.file.size,
      mime_type: data.vps?.mime_type ?? options.file.type,
      fileId: data.fileRecord?.id,
    };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : "Network error during upload",
    };
  }
}

/* ─── Core service: deleteFileFromVps ───────────────────────── */

export async function deleteFileFromVps(options: VpsDeleteOptions): Promise<VpsDeleteResult> {
  const { data: session } = await supabase.auth.getSession();
  const token = session?.session?.access_token;

  if (!token) {
    const err = { success: false, error: "User not authenticated" } as VpsDeleteResult;
    if (options.silent) return err;
    throw new Error(err.error);
  }

  if (!options.fileId && !options.path) {
    const err = { success: false, error: "Either fileId or path must be provided" } as VpsDeleteResult;
    if (options.silent) return err;
    throw new Error(err.error);
  }

  const functionUrl = `${supabaseUrl}/functions/v1/vps-delete-proxy`;

  try {
    const res = await fetch(functionUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        file_id: options.fileId,
        path: options.path,
        tenant_id: options.tenantId,
      }),
    });

    const data = await res.json();

    if (!res.ok) {
      const err = {
        success: false,
        error: data?.error || `Delete failed (HTTP ${res.status})`,
      } as VpsDeleteResult;
      if (options.silent) return err;
      throw new Error(err.error);
    }

    if (!data.success) {
      const err = {
        success: false,
        error: data?.error || "VPS rejected the delete",
      } as VpsDeleteResult;
      if (options.silent) return err;
      throw new Error(err.error);
    }

    return {
      success: true,
      message: data.vps?.message || "Deleted",
      deletedId: data.deletedId,
    };
  } catch (err) {
    const error = {
      success: false,
      error: err instanceof Error ? err.message : "Network error during delete",
    } as VpsDeleteResult;
    if (options.silent) return error;
    throw new Error(error.error);
  }
}

/* ─── Direct upload (bypass edge function — for public/anonymous) */

export async function uploadToVpsDirect(
  url: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<VpsUploadResult> {
  const formData = new FormData();
  formData.append("photo", file);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open("POST", url, true);

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          onProgress(percent);
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          if (data.success) {
            resolve({
              success: true,
              url: data.url,
              path: data.path,
              filename: data.file_name ?? file.name,
              size: data.file_size_bytes ?? file.size,
              mime_type: data.mime_type ?? file.type,
            });
          } else {
            reject(new Error(data.message || "Upload failed"));
          }
        } catch {
          reject(new Error("Invalid response from upload endpoint"));
        }
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error("Network error during upload"));
    xhr.send(formData);
  });
}

export async function deleteFromVpsDirect(
  url: string,
  path: string,
): Promise<VpsDeleteResult> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ path }),
  });

  if (!res.ok) {
    throw new Error(`Delete failed with status ${res.status}`);
  }

  const data = await res.json();
  return {
    success: data.success,
    message: data.message || "Deleted",
  };
}